import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { keyframes } from "@mui/system";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PublicIcon from "@mui/icons-material/Public";

// Subtle pulse animation for the indicator dot
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.3); opacity: 0.7; }
`;

// Shimmer animation for loading state
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

/**
 * Animated pill-style tab switcher with a modern, refined feel
 */
function AnimatedTabSwitcher({
  activeTab,
  onTabChange,
  yourRecipesCount = 0,
  webResultsCount = 0,
  webResultsLoading = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  
  // Accent colors matching the search bar
  const accentPrimary = isDark ? "#BB86FC" : "#6366f1";
  const accentSecondary = isDark ? "#03DAC6" : "#8b5cf6";
  const glowColor = isDark
    ? "rgba(187, 134, 252, 0.2)"
    : "rgba(99, 102, 241, 0.12)";

  const tabs = [
    {
      id: "recipes",
      label: "reciply",
      icon: MenuBookIcon,
      count: yourRecipesCount,
      showCount: yourRecipesCount > 0,
    },
    {
      id: "web",
      label: "web",
      icon: PublicIcon,
      count: webResultsCount,
      showCount: webResultsCount > 0 || webResultsLoading,
      isLoading: webResultsLoading && webResultsCount === 0,
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        p: "5px",
        borderRadius: "14px",
        background: isDark
          ? "linear-gradient(135deg, rgba(30, 30, 35, 0.8) 0%, rgba(20, 20, 25, 0.9) 100%)"
          : "linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(241, 245, 249, 0.95) 100%)",
        border: `1.5px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
        width: "fit-content",
        position: "relative",
        boxShadow: `0 2px 8px -2px rgba(0,0,0,${isDark ? "0.3" : "0.08"})`,
      }}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        const Icon = tab.icon;

        return (
          <Box
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 2,
              py: 0.75,
              borderRadius: "10px",
              cursor: "pointer",
              position: "relative",
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              background: isActive
                ? `linear-gradient(135deg, ${accentPrimary}15, ${accentSecondary}10)`
                : "transparent",
              border: isActive
                ? `1px solid ${accentPrimary}40`
                : "1px solid transparent",
              boxShadow: isActive
                ? `0 0 0 2px ${glowColor}, 0 2px 8px -2px rgba(0,0,0,${isDark ? "0.3" : "0.1"})`
                : "none",
              transform: isActive ? "scale(1)" : "scale(0.98)",
              "&:hover": {
                background: isActive
                  ? `linear-gradient(135deg, ${accentPrimary}15, ${accentSecondary}10)`
                  : isDark
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.03)",
                transform: "scale(1)",
              },
              "&:active": {
                transform: "scale(0.96)",
              },
            }}
          >
            <Icon
              sx={{
                fontSize: 15,
                color: isActive
                  ? accentPrimary
                  : theme.palette.text.secondary,
                transition: "all 0.25s ease",
                filter: isActive ? `drop-shadow(0 0 4px ${glowColor})` : "none",
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                fontWeight: isActive ? 600 : 400,
                fontSize: "0.75rem",
                letterSpacing: "-0.01em",
                color: isActive
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
                transition: "all 0.25s ease",
                whiteSpace: "nowrap",
              }}
            >
              {tab.label}
            </Typography>
            
            {/* Count badge */}
            {tab.showCount && (
              <Box
                sx={{
                  minWidth: 20,
                  height: 20,
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isActive
                    ? isDark
                      ? "rgba(187, 134, 252, 0.2)"
                      : "rgba(99, 102, 241, 0.15)"
                    : isDark
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.06)",
                  border: isActive
                    ? isDark
                      ? "1px solid rgba(187, 134, 252, 0.4)"
                      : "1px solid rgba(99, 102, 241, 0.3)"
                    : "none",
                  transition: "all 0.25s ease",
                  px: 0.5,
                }}
              >
                {tab.isLoading ? (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: isActive
                        ? accentPrimary
                        : `linear-gradient(90deg, transparent, ${theme.palette.text.secondary}, transparent)`,
                      backgroundSize: "200% 100%",
                      animation: isActive
                        ? `${pulse} 1s ease-in-out infinite`
                        : `${shimmer} 1.5s ease-in-out infinite`,
                    }}
                  />
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                      color: isActive
                        ? accentPrimary
                        : theme.palette.text.secondary,
                      lineHeight: 1,
                    }}
                  >
                    {tab.count}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default AnimatedTabSwitcher;

